/**
 * Time formatting utilities
 */

/**
 * Formats minutes into a human-readable duration string.
 *
 * Examples:
 * - 45 → "45 min"
 * - 60 → "1 hora"
 * - 90 → "1 hora 30 min"
 * - 120 → "2 horas"
 * - 185 → "3 horas 5 min"
 *
 * @param minutes - The duration in minutes
 * @param options - Formatting options
 * @returns Formatted duration string
 */
export function formatDuration(
  minutes: number,
  options: {
    short?: boolean; // Use short format (1h 30m instead of 1 hora 30 min)
    showZeroMinutes?: boolean; // Show "0 min" if minutes is 0
  } = {}
): string {
  const { short = false, showZeroMinutes = false } = options;

  if (minutes === 0) {
    return showZeroMinutes ? (short ? '0m' : '0 min') : '-';
  }

  if (minutes < 0) {
    return '-';
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return short ? `${mins}m` : `${mins} min`;
  }

  if (mins === 0) {
    if (short) {
      return `${hours}h`;
    }
    return hours === 1 ? '1 hora' : `${hours} horas`;
  }

  if (short) {
    return `${hours}h ${mins}m`;
  }

  const hoursText = hours === 1 ? '1 hora' : `${hours} horas`;
  return `${hoursText} ${mins} min`;
}

/**
 * Formats seconds into MM:SS or HH:MM:SS format
 *
 * @param seconds - The duration in seconds
 * @param options - Formatting options
 * @returns Formatted time string
 */
export function formatTimeFromSeconds(
  seconds: number,
  options: {
    showHours?: boolean; // Always show hours even if 0
  } = {}
): string {
  const { showHours = false } = options;

  if (seconds < 0) {
    return '00:00';
  }

  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const minsStr = mins.toString().padStart(2, '0');
  const secsStr = secs.toString().padStart(2, '0');

  if (hours > 0 || showHours) {
    const hoursStr = hours.toString().padStart(2, '0');
    return `${hoursStr}:${minsStr}:${secsStr}`;
  }

  return `${minsStr}:${secsStr}`;
}

/**
 * Formats minutes into HH:MM:SS wait time string.
 *
 * Examples:
 * - 0 → "00:00:00"
 * - 5 → "00:05:00"
 * - 45 → "00:45:00"
 * - 60 → "01:00:00"
 * - 90 → "01:30:00"
 * - 125.5 → "02:05:30"
 *
 * @param minutes - The wait time in minutes
 * @returns Formatted wait time string in HH:MM:SS
 */
export function formatWaitTime(minutes: number): string {
  if (minutes <= 0) return '00:00:00';

  const totalSeconds = Math.round(minutes * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
